import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import Swal from "sweetalert2";
import "./Login.scss";

import logo from "../../assets/logo.webp";
import agenda from "../../assets/agenda.webp";
import sombra from "../../assets/sombra.webp";
import { useAuth } from "../../auth/AuthContext";
import { usePageSeo } from "../../seo/usePageSeo";

const BLOQUEIO_KEY = "loginBloqueadoAte";
const MAX_TENTATIVAS = 5;
const BLOQUEIO_MS = 5 * 60 * 1000;

function getBloqueioRestante() {
  const ate = Number(localStorage.getItem(BLOQUEIO_KEY) || 0);
  const restante = ate - Date.now();
  return restante > 0 ? restante : 0;
}

export const Login = () => {
  const navigate = useNavigate();
  const { usuario, inicializando, entrar } = useAuth();

  const [loginDigitado, setLoginDigitado] = useState("");
  const [senhaDigitada, setSenhaDigitada] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [tentativasFalhas, setTentativasFalhas] = useState(0);
  const [bloqueioRestante, setBloqueioRestante] = useState(getBloqueioRestante);

  useEffect(() => {
    if (getBloqueioRestante() <= 0) return;
    const id = setInterval(() => {
      const r = getBloqueioRestante();
      setBloqueioRestante(r);
      if (r <= 0) {
        setTentativasFalhas(0);
        clearInterval(id);
      }
    }, 1000);
    return () => clearInterval(id);
  }, []);

  usePageSeo({
    title: "AgendaUni",
    description:
      "Crie, organize e gerencie suas atividades de forma simples e eficiente, em um só lugar.",
    path: "/",
  });

  if (inicializando) return null;
  if (usuario) return <Navigate to="/dashboard" replace />;

  const handleLogin = async (e) => {
    e.preventDefault();
    setCarregando(true);

    try {
      await entrar({ email: loginDigitado, senha: senhaDigitada });
      setTentativasFalhas(0);
      localStorage.removeItem(BLOQUEIO_KEY);

      await Swal.fire({
        title: "Acesso autorizado",
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
        timerProgressBar: true,
      });

      navigate("/dashboard", { replace: true });
    } catch (err) {
      setCarregando(false);

      if (err?.status !== 429) {
        const novasTentativas = tentativasFalhas + 1;
        setTentativasFalhas(novasTentativas);

        if (novasTentativas >= MAX_TENTATIVAS) {
          const ate = Date.now() + BLOQUEIO_MS;
          localStorage.setItem(BLOQUEIO_KEY, String(ate));
          setBloqueioRestante(BLOQUEIO_MS);
          Swal.fire({
            title: "Acesso bloqueado",
            text: "Muitas tentativas incorretas. Aguarde 5 minutos.",
            icon: "warning",
            confirmButtonText: "Entendido",
            confirmButtonColor: "#2E4A67",
          });
          return;
        }
      }

      // Mensagem única para qualquer falha de acesso — senha errada, login
      // errado, instituição desativada/excluída, usuário inativo. Não revela o
      // motivo (não enumera conta nem status da instituição). Rate-limit é a
      // única exceção: é sobre excesso de tentativas, não credencial.
      if (err?.status === 429) {
        Swal.fire({
          title: "Muitas tentativas",
          text: "Aguarde alguns minutos antes de tentar novamente.",
          icon: "warning",
          confirmButtonText: "Entendido",
          confirmButtonColor: "#2E4A67",
        });
      } else {
        Swal.fire({
          title: "Login ou senha incorretos",
          text: "Verifique seus dados e tente novamente.",
          icon: "error",
          confirmButtonText: "Entendido",
          confirmButtonColor: "#2E4A67",
        });
      }
    }
  };

  return (
    <div className="login-screen">
      <img src={sombra} className="bg-overlay" alt="" />
      <div className="corner-decoration top-left" />
      <div className="corner-decoration top-right" />
      <div className="corner-decoration bottom-left" />

      <header className="brand-header">
        {/* H1 lido por buscadores/IA; invisível na tela (sem mudança de design) */}
        <h1 className="sr-only">
          AgendaUni — Crie, organize e gerencie suas atividades de forma simples
          e eficiente, em um só lugar.
        </h1>
        <img src={logo} alt="AgendaUni" className="main-logo" />
      </header>

      <main className="content-wrapper">
        <section className="login-box">
          <h2 className="login-title">Acesse sua conta</h2>

          <form className="form-container" onSubmit={handleLogin}>
            <div className="input-group">
              <label htmlFor="login-username">Login</label>
              <input
                id="login-username"
                name="username"
                type="text"
                autoComplete="username"
                placeholder="Digite seu login"
                required
                value={loginDigitado}
                onChange={(e) => setLoginDigitado(e.target.value)}
              />
            </div>

            <div className="input-group">
              <label htmlFor="login-password">Senha</label>
              <div className="password-wrapper">
                <input
                  id="login-password"
                  name="password"
                  type={mostrarSenha ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Digite sua senha"
                  required
                  value={senhaDigitada}
                  onChange={(e) => setSenhaDigitada(e.target.value)}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setMostrarSenha((v) => !v)}
                  tabIndex={-1}
                  aria-label={mostrarSenha ? "Ocultar senha" : "Mostrar senha"}
                >
                  {mostrarSenha ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {bloqueioRestante > 0 && (
              <p className="login-error-msg" role="alert">
                Muitas tentativas. Tente novamente em{" "}
                {Math.ceil(bloqueioRestante / 1000)}s.
              </p>
            )}

            <div className="form-footer">
              <button
                type="button"
                className="forgot-link"
                onClick={() => navigate("/problemas-login")}
              >
                Problemas com login e/ou senha?
              </button>
            </div>

            <button
              type="submit"
              className="btn-login"
              disabled={carregando || bloqueioRestante > 0}
            >
              {carregando ? "Aguarde..." : "Entrar"}
            </button>

            <div className="bottom-lines" />
          </form>
        </section>
      </main>

      <div className="illustration-container">
        <img src={agenda} alt="Ilustração Agenda" className="agenda-img" />
      </div>
    </div>
  );
};
